import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { type Column, ListTable } from '@/components/admin/ListTable';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { ForbiddenState } from '@/components/admin/states';
import { EventDriveFilesBox } from '@/features/events/EventDriveFilesBox';
import { confirmMeetLogCsv, uploadMeetLogCsv, type CsvJob, type CsvRowResult, csvSampleUrl } from '@/features/csv/api';
import { fetchMembers } from '@/features/members/api';
import {
  fetchMeetMatchSummary,
  fetchMeetQueue,
  MEET_QUEUE_STATUS_LABELS,
  resolveMeetQueueItem,
  type MeetAttendanceLog,
} from '@/features/meet/api';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

/** Meet資料タブ: 参加ログCSV取込 + 確認キューの手動解決（指示書 §8・§12 M9. 事務局専用）. */
export function EventMeetTab({ eventId }: { eventId: string }): ReactElement {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [job, setJob] = useState<CsvJob | null>(null);
  const [reason, setReason] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 確認キューの解決でMeet用メールを学習したときの通知（D-070）
  const [learned, setLearned] = useState<string | null>(null);

  const isOffice = user?.active_role === 'office';
  // 確認が必要な行だけ（会員へ割当の操作を出す）
  const queueQuery = useQuery({
    queryKey: ['meet-queue', eventId],
    queryFn: () => fetchMeetQueue(eventId, 'needs_review'),
    enabled: isOffice,
  });
  // 自動突合・解決済みも含めた全件（入室・退室・滞在時間を確認するための一覧）
  const allLogsQuery = useQuery({
    queryKey: ['meet-queue', eventId, 'all'],
    queryFn: () => fetchMeetQueue(eventId),
    enabled: isOffice,
  });
  const summaryQuery = useQuery({
    queryKey: ['meet-summary', eventId],
    queryFn: () => fetchMeetMatchSummary(eventId),
    enabled: isOffice,
  });
  const membersQuery = useQuery({
    queryKey: ['members-picker'],
    queryFn: () => fetchMembers({ per_page: 100 }),
    enabled: isOffice,
  });

  if (!isOffice) {
    return <ForbiddenState message="Meet資料の確認は事務局のみ行えます。" />;
  }

  async function onFileSelected(file: File): Promise<void> {
    setBusy(true);
    setUploadError(null);
    setJob(null);
    try {
      setJob(await uploadMeetLogCsv(eventId, file));
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'アップロードに失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onConfirm(): Promise<void> {
    if (!job) return;
    setBusy(true);
    setUploadError(null);
    try {
      setJob(await confirmMeetLogCsv(job.id, reason));
      await queryClient.invalidateQueries({ queryKey: ['meet-queue', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['meet-summary', eventId] });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : '確定に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onResolve(log: MeetAttendanceLog, memberId: string | null): Promise<void> {
    setBusy(true);
    try {
      const resolved = await resolveMeetQueueItem(eventId, log.id, memberId);
      if (resolved.learned_meet_email) {
        // 次回から自動突合されることを伝える（黙って覚えると事務局が挙動を把握できない）
        setLearned(resolved.learned_meet_email);
      }
      await queryClient.invalidateQueries({ queryKey: ['meet-queue', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['meet-summary', eventId] });
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : '解決に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const hasErrors = (job?.summary?.error_count ?? 0) > 0;
  const isConfirmed = job?.status === 'confirmed';

  const columns: Column<MeetAttendanceLog>[] = [
    { key: 'email', header: 'メール', primary: true, render: (row) => row.email || '（空欄）' },
    { key: 'display_name', header: '表示名', render: (row) => row.display_name ?? '—' },
    {
      key: 'join_at',
      header: '参加時刻',
      render: (row) => (row.join_at ? formatDateTime(row.join_at) : '—'),
    },
    { key: 'status', header: '状態', render: (row) => MEET_QUEUE_STATUS_LABELS[row.queue_status] },
    {
      key: 'resolve',
      header: '会員へ割当',
      render: (row) => (
        <select
          disabled={busy}
          defaultValue=""
          onChange={(e) => {
            const value = e.target.value;
            if (value === '__reject__') void onResolve(row, null);
            else if (value) void onResolve(row, value);
          }}
          className="rounded-sm border border-wpborder px-3 py-2"
        >
          <option value="" disabled>
            選択してください
          </option>
          {(membersQuery.data?.items ?? []).map((member) => (
            <option key={member.id} value={member.id}>
              {member.name}（{member.member_id}）
            </option>
          ))}
          <option value="__reject__">出欠として扱わない</option>
        </select>
      ),
    },
  ];

  // 自動突合・要確認・解決済みを問わず全件を出す一覧。入室・退室・滞在時間まで確認できる
  // （確認キューは「要確認」だけに絞った操作用の一覧のため、別に持つ / D-172）
  const allLogColumns: Column<MeetAttendanceLog>[] = [
    {
      key: 'email',
      header: 'メール',
      primary: true,
      render: (row) => row.email || '（空欄）',
    },
    { key: 'display_name', header: '表示名', render: (row) => row.display_name ?? '—' },
    {
      key: 'join_at',
      header: '入室',
      render: (row) => (row.join_at ? formatDateTime(row.join_at) : '—'),
    },
    {
      key: 'leave_at',
      header: '退室',
      render: (row) => (row.leave_at ? formatDateTime(row.leave_at) : '—'),
    },
    {
      key: 'duration_seconds',
      header: '滞在',
      render: (row) =>
        row.duration_seconds !== null ? `${Math.round(row.duration_seconds / 60)}分` : '—',
    },
    {
      key: 'matched_member',
      header: '会員',
      render: (row) =>
        row.matched_member_id ? (
          <Link to={`/members/${row.matched_member_id}`} className="text-wpaccent hover:underline">
            {row.matched_member_name ?? '（会員詳細を見る）'}
          </Link>
        ) : (
          '—'
        ),
    },
    { key: 'status', header: '状態', render: (row) => MEET_QUEUE_STATUS_LABELS[row.queue_status] },
  ];

  return (
    <div className="flex flex-col gap-4">
      {uploadError && (
        <Notice kind="error" dismissible={false}>
          {uploadError}
        </Notice>
      )}
      {isConfirmed && (
        <Notice kind="success" dismissible={false}>
          取込を確定しました。完全一致した参加者は自動確定され、不一致は下の確認キューに入っています。
        </Notice>
      )}

      <MetaBox title="1. Meet参加ログCSVを取込">
        <p className="mb-2 text-wptext-muted">
          参加ログは、Meetを発行した<strong>主催者のGoogleアカウント</strong>
          でしかAPIから取得できません。運営で使う指定のGoogleアカウントで会議を発行してください。個人アカウントで発行した会議のログは取得できません。
        </p>
        <p className="mb-2 text-wptext-muted">
          はじめての場合は
          <a href={csvSampleUrl('meet-log')} className="text-wpaccent hover:underline">
            サンプルCSVをダウンロード
          </a>
          して形を確認できます（列: メール / 表示名 / 入室日時 / 退室日時 / 会議コード）。
        </p>
        <input
          type="file"
          accept=".csv,text/csv"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onFileSelected(file);
          }}
        />
        <p className="mt-2 text-xs text-wptext-muted">
          列: メール、表示名、入室日時、退室日時。確認済みメールと完全一致した参加者のみ自動確定します。不一致・電話参加・匿名表示は下の確認キューに入ります。
        </p>
      </MetaBox>

      {job && (
        <MetaBox title="2. プレビュー・確定">
          <ListTable<CsvRowResult>
            caption="Meetログプレビュー"
            rows={job.rows}
            columns={[
              { key: 'row_no', header: '行', render: (r) => r.row_no },
              {
                key: 'email',
                header: 'メール',
                primary: true,
                render: (r) => (r.data.email as string | undefined) || '（空欄）',
              },
              { key: 'action', header: '判定', render: (r) => r.action },
              {
                key: 'warnings',
                header: '警告',
                render: (r) => r.warnings.join(' / ') || '—',
              },
              {
                key: 'errors',
                header: 'エラー',
                render: (r) => (
                  <span className="text-wpnotice-error">{r.errors.join(' / ') || '—'}</span>
                ),
              },
            ]}
            rowKey={(row) => String(row.row_no)}
          />
          {!isConfirmed &&
            (hasErrors ? (
              <p className="mt-3 text-wpnotice-error">
                エラーが残っています。ファイルを修正して再アップロードしてください。
              </p>
            ) : (
              <div className="mt-3">
                <label htmlFor="meet-csv-reason" className="mb-1.5 block text-wptext-muted">
                  確定理由（監査ログに記録されます）
                </label>
                <textarea
                  id="meet-csv-reason"
                  required
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="mb-2 w-full max-w-xl rounded-sm border border-wpborder px-3 py-2"
                />
                <button
                  type="button"
                  disabled={busy || !reason.trim()}
                  onClick={() => void onConfirm()}
                  className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
                >
                  確定する
                </button>
              </div>
            ))}
        </MetaBox>
      )}

      {summaryQuery.data && (
        <MetaBox title="突合状況">
          <ul className="mb-2 flex flex-wrap gap-4">
            <li>取込ログ: {summaryQuery.data.total} 件</li>
            <li>自動突合: {summaryQuery.data.auto_matched} 件</li>
            <li>要確認: {summaryQuery.data.needs_review} 件</li>
            <li>手動で解決済み: {summaryQuery.data.resolved} 件</li>
          </ul>
          {summaryQuery.data.members_without_meet_email > 0 ? (
            <Notice kind="warning" dismissible={false}>
              対象者のうち <strong>{summaryQuery.data.members_without_meet_email} 名</strong>
              は「Meet用メール」が未登録です。未登録の受講生は毎回この確認キューに入ります。
              確認キューで会員に紐づけると、そのメールを自動で覚えるので、
              運用を重ねるほど要確認は減っていきます。
            </Notice>
          ) : (
            <p className="text-wptext-muted">
              対象者全員のMeet用メールが登録済みです。以降は自動突合が期待できます。
            </p>
          )}
        </MetaBox>
      )}

      {learned && (
        <Notice kind="success" onDismiss={() => setLearned(null)}>
          <strong>{learned}</strong> をこの会員のMeet用メールとして登録しました。
          次回以降はこのメールで自動突合されます。
        </Notice>
      )}

      <MetaBox title="参加ログ一覧（全件）">
        <p className="mb-2 text-wptext-muted">
          自動突合・要確認・解決済みを問わず、取り込んだ参加ログすべての入室・退室・滞在時間です。
          会員へ割当済みのものは名前から会員詳細に移動できます。
        </p>
        <ListTable<MeetAttendanceLog>
          caption="Meet参加ログ一覧"
          rows={allLogsQuery.data ?? []}
          columns={allLogColumns}
          rowKey={(row) => row.id}
          state={allLogsQuery.isPending ? 'loading' : allLogsQuery.isError ? 'error' : 'ready'}
          emptyTitle="参加ログはまだ取り込まれていません"
        />
      </MetaBox>

      <MetaBox
        title="確認キュー（不一致・電話参加・匿名表示）"
        badge={queueQuery.data?.length ?? 0}
        badgeLabel="要確認"
      >
        <ListTable<MeetAttendanceLog>
          caption="Meet確認キュー"
          rows={queueQuery.data ?? []}
          columns={columns}
          rowKey={(row) => row.id}
          state={queueQuery.isPending ? 'loading' : queueQuery.isError ? 'error' : 'ready'}
          emptyTitle="確認が必要な項目はありません"
        />
      </MetaBox>

      <EventDriveFilesBox eventId={eventId} />
    </div>
  );
}
